import React, { ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Expander from '@amzn/meridian/expander';
import Image from '@amzn/meridian/image';
import Input from '@amzn/meridian/input';
import SearchField from '@amzn/meridian/search-field';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import { BackgroundFormData, fetchAustinCases, getEmployeeInfo, setSearchedAlias } from 'src/store/newJmrPageSlice';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';
import { createSearchRegExp } from 'src/utils/searchUtils';
import { PAPI_FIELDS } from 'src/config/PapiFields';
import { BADGE_PHOTO_THUMBNAIL_URL } from 'src/config/ExternalLinks';
import { getExampleCaseIdText } from 'src/utils/helpers';
import { sortBasedOnStrategy, SortStrategy } from 'src/utils/sortStrategies';
import { stripOutLeadingHash } from 'src/utils/helpers';
import CaseSelection from 'src/components/shared/caseSelection/caseSelection';
import { AustinCase } from 'src/services/incident/types';
import { CaseSources, JmrFlowTypes } from 'src/utils/jmrUtils';
import RestrictionInformation from '../restrictionInformation/restrictionInformation';
import CaseSource from './caseSource/caseSource';

interface AssociateInformationProps {
  handleChange: (key: keyof BackgroundFormData, value: any) => void;
}

/**
 * Main component
 */
const AssociateInformation = ({ handleChange }: AssociateInformationProps) => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const {
    austinCases,
    isAUSTINDown,
    areAustinCasesFetched,
    jmrFlowType,
    searchedAlias,
    isLoadingPAPI,
    isLoadingIncident,
    placementId,
  } = useAppSelector((state) => state.newJmrPage);
  const backgroundFormData = useAppSelector((state) => state.newJmrPage.backgroundFormData);
  const { alias, caseSource, employeeID, fullName, job, managerAlias, managerName, caseIdValid } = backgroundFormData;
  const isCreatingNewAccommodation = jmrFlowType === JmrFlowTypes.CreateNewAccommodation;
  const isLoading = isLoadingPAPI || isLoadingIncident;
  const isAliasLoaded = fullName && alias === searchedAlias;

  useEffect(() => {
    if (params.alias) {
      handleChange('alias', params.alias.replace(/[^A-Za-z0-9]/g, ''));
    }
  }, []);

  // Update avatar image when backgroundFormData changes
  useEffect(() => {
    dispatch(setSearchedAlias(alias));
  }, [employeeID]);

  // Show Error Toast if no "Austin" cases found
  useEffect(() => {
    if (
      caseSource === CaseSources.Austin &&
      areAustinCasesFetched &&
      austinCases?.length === 0 &&
      searchedAlias?.length > 0 &&
      !isAUSTINDown
    ) {
      dispatch(
        createToast({
          type: 'error',
          message: 'No AUSTIN cases found for this alias.',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
  }, [caseSource, areAustinCasesFetched, austinCases, searchedAlias, isAUSTINDown]);

  // On "submit function"
  const submit = async () => {
    if (!backgroundFormData.caseSource) {
      dispatch(
        createToast({
          type: 'error',
          message: 'Case Source is required.',
          timeout: TOAST_TIMEOUT,
        }),
      );
    } else {
      await dispatch(getEmployeeInfo(alias));
      if (caseSource === CaseSources.Austin) {
        if (isCreatingNewAccommodation || !caseIdValid) {
          dispatch(fetchAustinCases({ alias }));
        } else if (!!placementId) {
          dispatch(fetchAustinCases({ placementId }));
        }
      }
    }
  };

  return (
    <Box>
      {/* Show Loader */}
      {isLoading && (
        <Box className="center-loader">
          <Loader />
        </Box>
      )}

      {/* Description */}
      <Column spacing="200" spacingInset="none none 450 none">
        <Text type="h400">Associate Information</Text>
        <Text type="b300" color="secondary">
          Search for the Associate using the alias field and magnifying glass. Complete any missing information.
        </Text>
      </Column>

      {/* Select Alias and Site */}
      <Row widths={['45%', '45%']} alignmentVertical="top" spacingInset="100 none 450 none">
        {/* Select Alias */}
        <Row>
          <CaseSource handleChange={handleChange} isNewJMR={isCreatingNewAccommodation} />
          <SearchField
            label="Alias"
            placeholder="Associate's alias"
            data-cy="new-jmr__background__alias__search"
            disabled={!isCreatingNewAccommodation || PAPI_FIELDS.includes('alias')}
            value={backgroundFormData.alias}
            onChange={(val) => handleChange('alias', val.replace(/[^A-Za-z0-9]/g, ''))}
            searchButton={true}
            onSubmit={submit}
          />
        </Row>

        {/* Select Site */}
        {!isLoading && isAliasLoaded && (
          <Row spacingInset="none 450" alignmentVertical="top" widths="fill">
            <Column spacing="200">
              <Text tag="label" type="b300" color="primary">
                Site Location
              </Text>
              <SelectSite handleChange={handleChange} />
            </Column>
          </Row>
        )}
      </Row>

      {/* Enter Associate Info */}
      {!isLoading && isAliasLoaded && (
        <>
          {/* 1st Row */}
          <Column>
            <Row width="100%" widths={['45%', '45%']} alignmentHorizontal="start" alignmentVertical="top">
              <Box type="outline" minHeight={140}>
                <Row widths={['fit', 'fill']} alignmentHorizontal="start" alignmentVertical="center">
                  <Column heights="fit" alignmentHorizontal="start" alignmentVertical="center">
                    <Box minWidth={120}>
                      <Image
                        data-cy="new-jmr__background__user-avatar"
                        viewportHeight="160px"
                        src={`${BADGE_PHOTO_THUMBNAIL_URL}${searchedAlias}`}
                      />
                    </Box>
                  </Column>
                  <Column>
                    <Text data-cy="new-jmr__background__username" type="h100">
                      {fullName}
                      <Text data-cy="new-jmr__background__job" type="b200">
                        {job}
                      </Text>
                    </Text>
                    <Text data-cy="new-jmr__background__employee-id" type="b200">
                      {`Employee ID: ${employeeID}`}
                      <Text data-cy="new-jmr__background__manager-name-and-alias" type="b200">
                        {`Manager: ${managerName} @${managerAlias}`}
                      </Text>
                    </Text>
                  </Column>
                </Row>
              </Box>
              <Column spacingInset="200 450">
                <InputField
                  data-cy="new-jmr__background__shift"
                  fieldName="shift"
                  label="Shift"
                  placeholder="Shift Code (i.e. DCA-0730)"
                  handleChange={handleChange}
                />
                <InputField
                  data-cy="new-jmr__background__shiftLength"
                  fieldName="shiftLength"
                  label="Normal Shift Length (hours)"
                  placeholder="Shift length by hours"
                  handleChange={handleChange}
                />
              </Column>
            </Row>
          </Column>
          <Divider spacingBefore="450" />

          {/* 2nd Row */}
          <Row spacingInset="450 none" alignmentVertical="top">
            <RestrictionInformation handleChange={handleChange} />
          </Row>
          <Divider spacingBefore="450" spacingAfter="450" />

          {/* Third Row */}
          {caseSource === CaseSources.Dali || caseSource === CaseSources.Bali ? (
            <>
              <Text type="h300">Case ID</Text>
              <Row widths="fill" spacingInset="300 none" alignmentVertical="top">
                <InputField
                  fieldName="caseID"
                  label=""
                  placeholder={getExampleCaseIdText(caseSource)}
                  handleChange={handleChange}
                />
              </Row>
            </>
          ) : (
            <Column spacing="400">
              <Text type="h300">{`AUSTIN cases associated with ${fullName.split(' ')[0]} `}</Text>
              <AustinCaseRows handleChange={handleChange} />
            </Column>
          )}
        </>
      )}
    </Box>
  );
};

/**
 * Site location selector.
 */
const SelectSite = (props: { handleChange: AssociateInformationProps['handleChange'] }) => {
  const { handleChange } = props;
  const { backgroundFormErrors, backgroundFormData, jmrFlowType } = useAppSelector((state) => state.newJmrPage);
  const { allowedSites } = useAppSelector((state) => state.sites);
  const { siteLocation } = backgroundFormData;

  // Setup the options for the site
  const [siteSearchQuery, setSiteSearchQuery] = useState();
  const siteSearchRegExp = createSearchRegExp(siteSearchQuery);
  const matchedSiteOptions = allowedSites
    ?.map((site) => ({ label: site.site, value: site.site }))
    .filter((option) => !siteSearchQuery || siteSearchRegExp.test(option.label));

  return (
    <Select
      data-cy="new-jmr__background__siteLocation"
      searchQuery={siteSearchQuery}
      onSearch={setSiteSearchQuery}
      placeholder="Associate's home site"
      value={siteLocation}
      onChange={(val) => handleChange('siteLocation', val)}
      error={backgroundFormErrors.siteLocation?.error}
      errorMessage={backgroundFormErrors.siteLocation?.errorMessage}
      disabled={jmrFlowType !== JmrFlowTypes.CreateNewAccommodation}
    >
      {matchedSiteOptions.map((option) => (
        <SelectOption
          data-cy="new-jmr__background__siteLocation-item"
          label={option.label}
          value={option.value}
          key={option.value}
        />
      ))}
      {!matchedSiteOptions.length && (
        <Column alignmentVertical="center" spacing="300" spacingInset="500">
          <Text alignment="center">No sites found</Text>
        </Column>
      )}
    </Select>
  );
};

/**
 * AustingCase Rows
 */
const AustinCaseRows = (props: { handleChange: AssociateInformationProps['handleChange'] }) => {
  const { handleChange } = props;
  const { austinCases, isAUSTINDown, jmrFlowType, showCaseIdRequiredText, backgroundFormData } = useAppSelector(
    (state) => state.newJmrPage,
  );
  const { site } = backgroundFormData;

  // Local state
  const [selectableAustinCases, setSelectableAustinCases] = useState<AustinCase[]>([]);
  const [unselectableAustinCases, setUnselectableAustinCases] = useState<AustinCase[]>([]);
  const [isSelectableCaseExpanderOpen, setIsSelectableCaseExpanderOpen] = useState(true);
  const [isUnselectableCaseExpanderOpen, setIsUnselectableCaseExpanderOpen] = useState(false);

  // Set/unset austin cases
  useEffect(() => {
    const filteredCases = austinCases?.filter(({ siteName }) => siteName === site?.site);
    setUnselectableAustinCases(
      sortBasedOnStrategy(
        SortStrategy.DATE,
        filteredCases?.filter((austinCase) => !austinCase.selectable),
        'updatedAt',
        'descending',
      ),
    );
    setSelectableAustinCases(
      sortBasedOnStrategy(
        SortStrategy.DATE,
        filteredCases?.filter((austinCase) => austinCase.selectable),
        'updatedAt',
        'descending',
      ),
    );
  }, [austinCases, site]);

  // If not creating a new accomodation
  if (jmrFlowType !== JmrFlowTypes.CreateNewAccommodation) {
    if (backgroundFormData.caseIdValid) {
      // Over here austinCases should only have one case.
      const austinCasesFiltered = austinCases.filter(
        (austinCase) => austinCase.caseId === stripOutLeadingHash(backgroundFormData.caseID),
      );
      return (
        <div>
          <CaseDetails austinCases={austinCasesFiltered} handleChange={handleChange} />
        </div>
      );
    }
  }

  // If the austin server is down
  if (isAUSTINDown) {
    return (
      <Row spacingInset="400 none">
        <Alert data-cy="new-jmr__austin-alert" type="warning">
          <Text type="h100">AUSTIN Server Unavailable</Text>
          <Text>
            AUSTIN server unavailable. Please proceed with current JMR creation and update this placement with the
            correct AUSTIN case when connection is restored.
          </Text>
        </Alert>
      </Row>
    );
  }

  if (!site && austinCases?.length > 0) {
    return <Text>Please select a Site Location</Text>;
  }

  if (selectableAustinCases?.length === 0 && unselectableAustinCases?.length === 0) {
    return <CaseIdRequiredText />;
  }

  return (
    <>
      {showCaseIdRequiredText && <CaseIdRequiredText />}

      {/* Selectable Austin Cases */}
      {selectableAustinCases?.length > 0 && (
        <Column>
          <Row></Row>
          <Expander
            title="Selectable cases (choose one case to continue):"
            open={isSelectableCaseExpanderOpen}
            onChange={setIsSelectableCaseExpanderOpen}
            type="list"
          >
            <CaseDetails austinCases={selectableAustinCases} handleChange={handleChange} />
          </Expander>
        </Column>
      )}

      {/* Unselectable Austin Cases */}
      {unselectableAustinCases?.length > 0 && (
        <Column>
          <Row></Row>
          <Expander
            title="Unselectable cases:"
            open={isUnselectableCaseExpanderOpen}
            onChange={setIsUnselectableCaseExpanderOpen}
            type="list"
          >
            <CaseDetails
              austinCases={unselectableAustinCases}
              unselectableOverride={true}
              handleChange={handleChange}
            />
          </Expander>
        </Column>
      )}
    </>
  );
};

/**
 * CaseDetails Component
 */
const CaseDetails = (props: {
  austinCases: AustinCase[];
  unselectableOverride?: boolean;
  handleChange: AssociateInformationProps['handleChange'];
}) => {
  const { austinCases, unselectableOverride = false, handleChange } = props;
  const { backgroundFormData } = useAppSelector((state) => state.newJmrPage);
  const { placementId } = useParams();
  return (
    <Column spacing="400">
      {austinCases.map((austinCase, i) => (
        <CaseSelection
          key={i}
          unselectableOverride={unselectableOverride}
          austinCase={austinCase}
          handleChange={() => handleChange('caseID', austinCase.caseId)}
          selectedCaseId={stripOutLeadingHash(backgroundFormData.caseID)}
          currentAccommodationId={placementId ? +placementId : undefined}
        />
      ))}
    </Column>
  );
};

/**
 * CaseIdRequiredText
 */
const CaseIdRequiredText = () => (
  <Row spacingInset="400 none">
    <Alert data-cy="new-jmr__austin-alert" type="error">
      <Text type="h100">AUSTIN case ID is required</Text>
      <Text>
        Please select an available AUSTIN case ID associated with this alias and site location. If no AUSTIN case ID is
        available, please select "Yes" for "Does this case require a Return to Work (RTW) placement? on the "Medical
        Attachments" page of the AUSTIN case. Reminder: you must save and submit within AUSTIN after uploading an RFI.
      </Text>
    </Alert>
  </Row>
);

/**
 * Custom input field
 */
const InputField = (props: {
  fieldName: keyof BackgroundFormData;
  label: string;
  placeholder: string;
  className?: string;
  children?: ReactNode;
  handleChange: AssociateInformationProps['handleChange'];
}) => {
  const { fieldName, label, placeholder, className, children, handleChange } = props;
  const { backgroundFormData, backgroundFormErrors } = useAppSelector((state) => state.newJmrPage);
  return (
    <Column spacing="200" className={className}>
      <Text tag="label" type="b300" color="primary">
        {label}
      </Text>
      <Input
        data-cy={`new-jmr__background__${fieldName}`}
        type="text"
        placeholder={placeholder}
        value={backgroundFormData[fieldName] as string}
        onChange={(value) => handleChange(fieldName, value)}
        error={backgroundFormErrors[fieldName]?.error}
        errorMessage={backgroundFormErrors[fieldName]?.errorMessage}
      >
        {children}
      </Input>
    </Column>
  );
};

export default AssociateInformation;
